import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './MyAccount.css';
import './SchoolDashboard.css';

function MyAccount() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('schoolAuthenticated');
    const storedSchoolData = localStorage.getItem('schoolData');
    
    if (!isAuthenticated || !storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    
    // User is authenticated, load school data
    const parsedData = JSON.parse(storedSchoolData);
    setSchoolData(parsedData);
    setFormData({
      school_name: parsedData.school_name || '',
      school_type: parsedData.school_type || 'public',
      email: parsedData.email || '',
      address: parsedData.address || '',
      city: parsedData.city || '',
      state: parsedData.state || '',
      zip_code: parsedData.zip_code || '',
      phone: parsedData.phone || '',
      principal_name: parsedData.principal_name || '',
      principal_email: parsedData.principal_email || '',
      total_students: parsedData.total_students || 0,
      total_teachers: parsedData.total_teachers || 0,
      website: parsedData.website || '',
      description: parsedData.description || ''
    });
    setLoading(false);
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      sessionStorage.setItem('schoolLoggedOut', 'true');
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      localStorage.removeItem('schoolRememberEmail');
      localStorage.removeItem('schoolRememberPassword');
      navigate('/schoollogin');
    } catch (error) {
      console.error('Logout error:', error);
      sessionStorage.setItem('schoolLoggedOut', 'true');
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      localStorage.removeItem('schoolRememberEmail');
      localStorage.removeItem('schoolRememberPassword');
      navigate('/schoollogin');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.school_name.trim()) errors.school_name = 'School name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.address.trim()) errors.address = 'Address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.principal_name.trim()) errors.principal_name = 'Principal name is required';
    if (!formData.principal_email.trim()) errors.principal_email = 'Principal email is required';
    
    // Email validation
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (formData.principal_email && !emailRegex.test(formData.principal_email)) {
      errors.principal_email = 'Please enter a valid email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Update school data in the database
      const { data, error } = await supabase
        .from('schools')
        .update({
          school_name: formData.school_name,
          school_type: formData.school_type,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          phone: formData.phone,
          principal_name: formData.principal_name,
          principal_email: formData.principal_email,
          total_students: parseInt(formData.total_students),
          total_teachers: parseInt(formData.total_teachers),
          website: formData.website,
          description: formData.description,
          updated_at: new Date().toISOString()
        })
        .eq('email', schoolData.email)
        .select();

      if (error) {
        console.error('Error updating school data:', error);
        alert('Failed to update school information. Please try again.');
        return;
      }

      // Update localStorage with new data
      const updatedSchoolData = { ...schoolData, ...formData };
      localStorage.setItem('schoolData', JSON.stringify(updatedSchoolData));
      setSchoolData(updatedSchoolData);
      
      setSuccessMessage('School information updated successfully!');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error updating school data:', error);
      alert('Failed to update school information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData({
      school_name: schoolData.school_name || '',
      school_type: schoolData.school_type || 'public',
      email: schoolData.email || '',
      address: schoolData.address || '',
      city: schoolData.city || '',
      state: schoolData.state || '',
      zip_code: schoolData.zip_code || '',
      phone: schoolData.phone || '',
      principal_name: schoolData.principal_name || '',
      principal_email: schoolData.principal_email || '',
      total_students: schoolData.total_students || 0,
      total_teachers: schoolData.total_teachers || 0,
      website: schoolData.website || '',
      description: schoolData.description || ''
    });
    setFormErrors({});
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading account information...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access your account.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>👤 My Account</h1>
          <p>View and manage your school information</p>
        </div>

        <div className="my-account-container">
          {successMessage && (
            <div className="success-message">
              <span className="success-icon">✅</span>
              {successMessage}
            </div>
          )}

          <div className="account-info-card">
            <div className="card-header">
              <h2>School Information</h2>
              {!isEditing && (
                <button 
                  className="edit-btn"
                  onClick={() => setIsEditing(true)}
                >
                  <span className="edit-icon">✏️</span>
                  Edit Information
                </button>
              )}
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmit} className="edit-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="school_name">School Name *</label>
                    <input
                      type="text"
                      id="school_name"
                      name="school_name"
                      value={formData.school_name}
                      onChange={handleInputChange}
                      className={formErrors.school_name ? 'error' : ''}
                    />
                    {formErrors.school_name && <span className="error-text">{formErrors.school_name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="school_type">School Type</label>
                    <select
                      id="school_type"
                      name="school_type"
                      value={formData.school_type}
                      onChange={handleInputChange}
                    >
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                      <option value="charter">Charter</option>
                      <option value="international">International</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={formErrors.email ? 'error' : ''}
                    />
                    {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={formErrors.phone ? 'error' : ''}
                    />
                    {formErrors.phone && <span className="error-text">{formErrors.phone}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="website">Website</label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Address *</label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={formErrors.address ? 'error' : ''}
                      rows="3"
                    />
                    {formErrors.address && <span className="error-text">{formErrors.address}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={formErrors.city ? 'error' : ''}
                    />
                    {formErrors.city && <span className="error-text">{formErrors.city}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State *</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={formErrors.state ? 'error' : ''}
                    />
                    {formErrors.state && <span className="error-text">{formErrors.state}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="zip_code">ZIP Code</label>
                    <input
                      type="text"
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="principal_name">Principal Name *</label>
                    <input
                      type="text"
                      id="principal_name"
                      name="principal_name"
                      value={formData.principal_name}
                      onChange={handleInputChange}
                      className={formErrors.principal_name ? 'error' : ''}
                    />
                    {formErrors.principal_name && <span className="error-text">{formErrors.principal_name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="principal_email">Principal Email *</label>
                    <input
                      type="email"
                      id="principal_email"
                      name="principal_email"
                      value={formData.principal_email}
                      onChange={handleInputChange}
                      className={formErrors.principal_email ? 'error' : ''}
                    />
                    {formErrors.principal_email && <span className="error-text">{formErrors.principal_email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="total_students">Total Students</label>
                    <input
                      type="number"
                      id="total_students"
                      name="total_students"
                      value={formData.total_students}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="total_teachers">Total Teachers</label>
                    <input
                      type="number"
                      id="total_teachers"
                      name="total_teachers"
                      value={formData.total_teachers}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Brief description about your school..."
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="info-display">
                <div className="info-grid">
                  <div className="info-item">
                    <label>School Name</label>
                    <span>{schoolData.school_name || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>School Type</label>
                    <span>{schoolData.school_type ? schoolData.school_type.charAt(0).toUpperCase() + schoolData.school_type.slice(1) : 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>Email</label>
                    <span>{schoolData.email || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>Phone</label>
                    <span>{schoolData.phone || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>Website</label>
                    <span>
                      {schoolData.website ? (
                        <a href={schoolData.website} target="_blank" rel="noopener noreferrer">
                          {schoolData.website}
                        </a>
                      ) : 'Not provided'}
                    </span>
                  </div>
                  
                  <div className="info-item">
                    <label>Address</label>
                    <span>{schoolData.address || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>City</label>
                    <span>{schoolData.city || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>State</label>
                    <span>{schoolData.state || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>ZIP Code</label>
                    <span>{schoolData.zip_code || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>Principal Name</label>
                    <span>{schoolData.principal_name || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>Principal Email</label>
                    <span>{schoolData.principal_email || 'Not provided'}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>Total Students</label>
                    <span>{schoolData.total_students || 0}</span>
                  </div>
                  
                  <div className="info-item">
                    <label>Total Teachers</label>
                    <span>{schoolData.total_teachers || 0}</span>
                  </div>
                </div>
                
                {schoolData.description && (
                  <div className="info-item full-width">
                    <label>Description</label>
                    <p className="description-text">{schoolData.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyAccount;
